import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, XCircle, AlertTriangle, ArrowRight } from 'lucide-react';
import { runAuthVerification, verifySupabaseConfig } from '../utils/authVerification';
import { runSystemDiagnostics, testUserRegistration, getConfigurationInfo } from '../utils/debugUtils';
import { useAuth } from '../context/AuthContext';

const VerifyAuth = () => {
  const { user, isAuthenticated } = useAuth();
  const [verificationStatus, setVerificationStatus] = useState<'idle' | 'running' | 'success' | 'error'>('idle');
  const [results, setResults] = useState<any>(null);
  const [configStatus, setConfigStatus] = useState<any>(null);
  const [diagnostics, setDiagnostics] = useState<any[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    // Check configuration on component mount
    const config = verifySupabaseConfig();
    setConfigStatus(config);
    
    // Get detailed configuration info
    const configInfo = getConfigurationInfo();
    console.log('📋 Información de configuración:', configInfo);
  }, []);

  const handleRunVerification = async () => {
    setVerificationStatus('running');
    try {
      const result = await runAuthVerification();
      setResults(result);
      setVerificationStatus(result.success ? 'success' : 'error');
    } catch (error) {
      console.error('Verification failed:', error);
      setResults({ success: false, error });
      setVerificationStatus('error');
    }
  };

  const handleRunDiagnostics = async () => {
    try {
      console.log('🔍 Ejecutando diagnósticos del sistema...');
      const diagnosticResults = await runSystemDiagnostics();
      setDiagnostics(diagnosticResults);
      console.log('📊 Resultados de diagnósticos:', diagnosticResults);
    } catch (error) {
      console.error('Error en diagnósticos:', error);
      setDiagnostics([{
        test: 'Sistema',
        status: 'error',
        message: 'Error ejecutando diagnósticos',
        details: error
      }]);
    }
  };

  const handleTestRegistration = async () => {
    try {
      console.log('🧪 Probando registro de usuario...');
      const testResults = await testUserRegistration();
      console.log('📊 Resultados del test de registro:', testResults);
      alert(`Test completado. Revisa la consola para ver los resultados detallados.`);
    } catch (error) {
      console.error('Error en test de registro:', error);
      alert('Error ejecutando test de registro. Revisa la consola.');
    }
  };
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Verificación de Autenticación
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Esta herramienta verifica la configuración de autenticación y registro de usuarios
          </p>
        </div>

        {/* Configuration Status */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg mb-6 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              Estado de Configuración
            </h2>
          </div>
          <div className="px-6 py-4">
            {configStatus ? (
              <div className="space-y-4">
                <div className="flex items-center">
                  {configStatus.isConfigured ? (
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500 mr-2" />
                  )}
                  <span className="text-gray-700 dark:text-gray-300">
                    Supabase configurado: {configStatus.isConfigured ? 'Sí' : 'No'}
                  </span>
                </div>
                <div className="flex items-center">
                  {configStatus.supabaseUrl && !configStatus.supabaseUrl.includes('Not configured') ? (
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500 mr-2" />
                  )}
                  <span className="text-gray-700 dark:text-gray-300">
                    URL de Supabase: {configStatus.supabaseUrl.includes('http') ? 'Configurado' : 'No configurado'}
                  </span>
                </div>
                <div className="flex items-center">
                  {configStatus.hasAnonKey ? (
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500 mr-2" />
                  )}
                  <span className="text-gray-700 dark:text-gray-300">
                    Clave anónima: {configStatus.hasAnonKey ? 'Configurada' : 'No configurada'}
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">Cargando información de configuración...</p>
            )}
          </div>
        </div>

        {/* Authentication Status */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg mb-6 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              Estado de Autenticación
            </h2>
          </div>
          <div className="px-6 py-4">
            <div className="space-y-4">
              <div className="flex items-center">
                {isAuthenticated ? (
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-yellow-500 mr-2" />
                )}
                <span className="text-gray-700 dark:text-gray-300">
                  Estado: {isAuthenticated ? 'Autenticado' : 'No autenticado'}
                </span>
              </div>
              {isAuthenticated && user && (
                <>
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    <span className="text-gray-700 dark:text-gray-300">
                      Usuario: {user.name} ({user.email})
                    </span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    <span className="text-gray-700 dark:text-gray-300">
                      Rol: {user.role}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Verification Tool */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              Herramienta de Verificación
            </h2>
          </div>
          <div className="px-6 py-4">
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Esta herramienta verificará:
            </p>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6">
              <li>Conexión a Supabase</li>
              <li>Configuración de autenticación</li>
              <li>Proceso de registro</li>
            </ul>

            <div className="flex justify-center">
              <button
                onClick={handleRunVerification}
                disabled={verificationStatus === 'running'}
                className={`px-4 py-2 rounded-md font-medium ${
                  verificationStatus === 'running'
                    ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed'
                    : 'bg-green-500 hover:bg-green-600 text-white'
                } transition-colors duration-200`}
              >
                {verificationStatus === 'running' ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Verificando...
                  </span>
                ) : (
                  'Ejecutar Verificación'
                )}
              </button>
            </div>

              <button
                onClick={handleRunDiagnostics}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md font-medium transition-colors duration-200"
              >
                Diagnósticos Completos
              </button>

              <button
                onClick={handleTestRegistration}
                className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-md font-medium transition-colors duration-200"
              >
                Test de Registro
              </button>
            {/* Results */}
            {results && (
              <div className={`mt-6 p-4 rounded-md ${
                results.success 
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' 
                  : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
              }`}>
                <div className="flex items-start">
                  {results.success ? (
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5" />
                  )}
                  <div>
                    <h3 className={`text-sm font-medium ${
                      results.success ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'
                    }`}>
                      {results.success ? 'Verificación exitosa' : 'Verificación fallida'}
                    </h3>
                    {!results.success && results.error && (
                      <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                        Error: {results.error.toString()}
                      </p>
                    )}
                    {results.success && (
                      <p className="mt-1 text-sm text-green-700 dark:text-green-300">
                        La configuración de autenticación está correcta.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
            {/* Diagnostics Results */}
            {diagnostics.length > 0 && (
              <div className="mt-6 space-y-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Resultados de Diagnósticos
                </h3>
                {diagnostics.map((diagnostic, index) => (
                  <div key={index} className={`p-4 rounded-md border ${
                    diagnostic.status === 'success' 
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                      : diagnostic.status === 'warning'
                      ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                      : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                  }`}>
                    <div className="flex items-start">
                      {diagnostic.status === 'success' ? (
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                      ) : diagnostic.status === 'warning' ? (
                        <AlertTriangle className="h-5 w-5 text-yellow-500 mr-2 mt-0.5" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5" />
                      )}
                      <div>
                        <h4 className={`text-sm font-medium ${
                          diagnostic.status === 'success' ? 'text-green-800 dark:text-green-200' :
                          diagnostic.status === 'warning' ? 'text-yellow-800 dark:text-yellow-200' :
                          'text-red-800 dark:text-red-200'
                        }`}>
                          {diagnostic.test}: {diagnostic.message}
                        </h4>
                        {diagnostic.details && (
                          <details className="mt-2">
                            <summary className="cursor-pointer text-xs text-gray-600 dark:text-gray-400">
                              Ver detalles
                            </summary>
                            <pre className="mt-1 text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 p-2 rounded overflow-x-auto">
                              {JSON.stringify(diagnostic.details, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>

        {/* Advanced Tools */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center justify-between w-full text-left"
            >
              <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                Herramientas Avanzadas
              </h2>
              <span className="text-gray-500">
                {showAdvanced ? '−' : '+'}
              </span>
            </button>
          </div>
          {showAdvanced && (
            <div className="px-6 py-4">
              <div className="space-y-4">
                <div>
                  <h3 className="text-md font-medium text-gray-900 dark:text-white mb-2">
                    Diagnósticos del Sistema
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Ejecuta una verificación completa de todos los componentes del sistema.
                  </p>
                  <button
                    onClick={handleRunDiagnostics}
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md font-medium transition-colors duration-200"
                  >
                    Ejecutar Diagnósticos
                  </button>
                </div>

                <div>
                  <h3 className="text-md font-medium text-gray-900 dark:text-white mb-2">
                    Test de Registro de Usuario
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Prueba el proceso completo de registro con un usuario de prueba.
                  </p>
                  <button
                    onClick={handleTestRegistration}
                    className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-md font-medium transition-colors duration-200"
                  >
                    Probar Registro
                  </button>
                </div>

                <div>
                  <h3 className="text-md font-medium text-gray-900 dark:text-white mb-2">
                    Información de Configuración
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Revisa la consola del navegador (F12) para ver información detallada de configuración.
                  </p>
                  <button
                    onClick={() => {
                      const info = getConfigurationInfo();
                      console.log('📋 Información completa de configuración:', info);
                      alert('Información mostrada en la consola. Presiona F12 para verla.');
                    }}
                    className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-md font-medium transition-colors duration-200"
                  >
                    Mostrar Info de Config
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* Navigation Links */}
        <div className="mt-8 flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
          <Link
            to="/register"
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
          >
            Ir a Registro
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
          <Link
            to="/login"
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            Ir a Login
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
          <Link
            to="/mi-perfil"
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
          >
            Ir a Perfil
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VerifyAuth;